import { TranslationKey } from './../translation/TranslationKey';
import { VentoryColor } from '../../VentoryUI/util/color.util';
import { CustomFieldEntitySubType } from '../custom_field/customField';
import { ProductTransactionSpecifiers } from '../productTransaction';
import { t, translate } from '../translation/Translator';
import { Order, OrderStatus, OrderType } from './order';
import { PdfTemplateEntitySubtype } from '../pdf_template';

export namespace OrderUtil {
  export const SAP_IDENTIFIER = '001';

  export function hasToStockLocation(order: Order): boolean {
    switch (order.type) {
      case OrderType.inbound:
      case OrderType.replenish:
      case OrderType.move:
        return true;
      case OrderType.outbound:
      case OrderType.disposal:
        return false;
    }
  }

  export function hasFromStockLocation(order: Order): boolean {
    switch (order.type) {
      case OrderType.inbound:
      case OrderType.replenish:
        return false;
      case OrderType.outbound:
      case OrderType.move:
      case OrderType.disposal:
        return true;
    }
  }

  export function determineStockLocation(order: Order): string {
    switch (order.type) {
      case OrderType.inbound:
      case OrderType.replenish:
        return order.toStockLocationId!;
      case OrderType.outbound:
      case OrderType.move:
      case OrderType.disposal:
        return order.fromStockLocationId!;
    }
  }

  export function orderSpecifierHash(specifier: ProductTransactionSpecifiers) {
    return orderSpecifierHashByValue(
      specifier.pmdId,
      specifier.containerId || specifier.toContainerId || specifier.fromContainerId,
    );
  }

  export function orderSpecifierHashByValue(pmdId?: string, containerId?: string) {
    return `${pmdId || ''}|${containerId || ''}`;
  }

  export const typeToString = (type: OrderType) => {
    switch (type) {
      case OrderType.inbound:
        return translate({ key: TranslationKey.inbound });
      case OrderType.outbound:
        return translate({ key: TranslationKey.outbound });
      case OrderType.disposal:
        return translate({ key: TranslationKey.disposal });
      case OrderType.replenish:
        return translate({ key: TranslationKey.replenish });
      case OrderType.move:
        return translate({ key: TranslationKey.move });
    }
  };

  export const typeToEntitySubType = (type: OrderType): CustomFieldEntitySubType => {
    switch (type) {
      case OrderType.inbound:
        return CustomFieldEntitySubType.inbound;
      case OrderType.outbound:
        return CustomFieldEntitySubType.outbound;
      case OrderType.replenish:
        return CustomFieldEntitySubType.replenish;
      case OrderType.move:
        return CustomFieldEntitySubType.move;
      case OrderType.disposal:
        return CustomFieldEntitySubType.disposal;
    }
  };

  export const statusToString = (status: OrderStatus): string => {
    switch (status) {
      case OrderStatus.open:
        return t(TranslationKey.open).singular.label;
      case OrderStatus.created:
        return t(TranslationKey.created).singular.label;
      case OrderStatus.inProgress:
        return t(TranslationKey.inProgress).singular.label;
      case OrderStatus.complete:
        return t(TranslationKey.completed).singular.label;
      case OrderStatus.shipped:
        return t(TranslationKey.shipped).singular.label;
      case OrderStatus.arrived:
        return t(TranslationKey.arrived).singular.label;
      case OrderStatus.cancelled:
        return t(TranslationKey.cancelled).singular.label;
      case OrderStatus.failed:
        return t(TranslationKey.failed).singular.label;
      case OrderStatus.released:
        return t(TranslationKey.released).singular.label;
      case OrderStatus.disabled:
        return t(TranslationKey.disabled).singular.label;
      case OrderStatus.pickingComplete:
        return t(TranslationKey.pickingComplete).singular.label;
    }
  };

  export const statusToColorStyle = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.open:
      case OrderStatus.created:
        return {
          color: VentoryColor.green700,
          backgroundColor: VentoryColor.green50,
        };
      case OrderStatus.released:
        return {
          color: VentoryColor.blue700,
          backgroundColor: VentoryColor.blue50,
        };
      case OrderStatus.inProgress:
        return {
          color: VentoryColor.yellow700,
          backgroundColor: VentoryColor.yellow50,
        };
      case OrderStatus.complete:
        return {
          color: VentoryColor.green500,
          backgroundColor: VentoryColor.green50,
        };
      case OrderStatus.cancelled:
        return {
          color: VentoryColor.red700,
          backgroundColor: VentoryColor.red50,
        };
      case OrderStatus.disabled:
        return {
          color: VentoryColor.grey700,
          backgroundColor: VentoryColor.grey50,
        };
      case OrderStatus.pickingComplete:
        return {
          color: VentoryColor.blue700,
          backgroundColor: VentoryColor.blue50,
        };
      default:
        return {
          color: VentoryColor.grey700,
          backgroundColor: VentoryColor.grey50,
        };
    }
  };

  export const orderTypeToPdfTemplateEntitySubtype = (type: OrderType): PdfTemplateEntitySubtype => {
    switch (type) {
      case OrderType.inbound:
      case OrderType.replenish:
        return PdfTemplateEntitySubtype.inbound;
      case OrderType.outbound:
        return PdfTemplateEntitySubtype.outbound;
      case OrderType.move:
        return PdfTemplateEntitySubtype.move;
      case OrderType.disposal:
        return PdfTemplateEntitySubtype.disposal;
    }
  };
}
